import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import type { RoutingConfigAssignmentRecord } from '@nadoutong/contracts';
import { CustomerRef } from '../../components/identity/CustomerRef.tsx';
import { useT } from '../../i18n/index.ts';

/**
 * Customers assigned to one routing config. Split out of
 * `TenantRoutingConfigSheet` by #1493 (size budget): this is the assignment
 * surface, not the routing-pool surface, and it shares no state with the
 * SKU tree / target pool above it.
 *
 * `assignedOrgCount` is the SERVER's count and is deliberately not derived
 * from the rows below it: the sheet is handed the page's assignment list,
 * which may be paginated or stale relative to the config record, and the two
 * numbers disagreeing is information rather than a bug to paper over.
 */
export function TenantRoutingAssignedCustomers({
  configId,
  assignedOrgCount,
  assignments,
}: {
  readonly configId: string;
  readonly assignedOrgCount: number;
  readonly assignments: readonly RoutingConfigAssignmentRecord[];
}): JSX.Element {
  const t = useT();
  const scoped = useMemo(() => {
    const rows = assignments.filter(
      (assignment) => assignment.config_id === configId && assignment.scope_type === 'org',
    );
    const byOrg = new Map<string, (typeof rows)[number]>();
    for (const row of rows) {
      const current = byOrg.get(row.scope_id);
      if (current === undefined || (row.priority ?? 0) < (current.priority ?? 0)) {
        byOrg.set(row.scope_id, row);
      }
    }
    return [...byOrg.values()].sort((left, right) =>
      (left.priority ?? 0) - (right.priority ?? 0) || left.scope_id.localeCompare(right.scope_id)
    );
  }, [assignments, configId]);

  return (
    <section className="rounded-lg border border-line-soft bg-well p-4" data-testid="tenant-routing-config-customers">
      <div className="flex items-center justify-between gap-2">
        <h3 className="m-0 text-body font-medium text-paper">
          {t('tenant_routing.configs.assigned_customers')}
        </h3>
        <span className="font-mono text-dense text-paper-low" data-testid="tenant-routing-config-customers-count">
          {assignedOrgCount}
        </span>
      </div>
      {scoped.length === 0 ? (
        <p className="m-0 mt-3 rounded bg-raised px-3 py-2 text-dense text-paper-low">
          {t('tenant_routing.configs.no_assignments')}
        </p>
      ) : (
        <div className="mt-3 grid gap-2" data-testid="tenant-routing-config-assignments">
          {scoped.map((assignment) => (
            <Link
              key={assignment.scope_id}
              to={`/crm/customers/${encodeURIComponent(assignment.scope_id)}?tab=custom_routes`}
              className="flex items-center justify-between gap-3 rounded-md bg-raised px-3 py-2 text-dense text-paper transition-colors duration-120 hover:bg-active"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className="font-mono text-micro text-paper-low">
                  {t('tenant_routing.configs.assignment_position', {
                    position: String((assignment.priority ?? 0) + 1),
                  })}
                </span>
                <span className="font-mono">
                  {assignment.scope_type === 'org' ? (
                    <CustomerRef orgId={assignment.scope_id} />
                  ) : (
                    assignment.scope_id
                  )}
                </span>
              </span>
              <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
